import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "wouter";
import { CarLogo } from "../components/common/CarLogo.js";
import { ErpShell } from "../components/layout/ErpShell.js";
import { FILTER_INPUT, FilterField, FilterPanel } from "../components/common/FilterBar.js";
import { useModals } from "../components/common/ModalsProvider.js";
import { useAuth } from "../hooks/useAuth.js";
import { useCanEdit } from "../hooks/useCanEdit.js";
import { useSelectedCompany } from "../hooks/useSelectedCompany.js";
import { formatMoney } from "../lib/format-fr.js";
import { trpc } from "../lib/trpc.js";
import { useT } from "../i18n/index.js";
import { VEHICLE_STATUS_STYLE as STATUS_STYLE } from "../lib/vehicle-status.js";
import { VehicleHistoryModal } from "../components/domain/VehicleHistoryModal.js";
import { Paginacao } from "../components/common/Paginacao.js";

const PAGE_SIZE = 50;

interface VehicleForm {
  customerId: number | null;
  customerSearch: string;
  plate: string;
  vin: string;
  /**
   * Marca: `makeId` preenchido = vinculada ao catálogo; null = texto livre.
   * Mesmo padrão para modelo. O texto (`make`/`model`) é sempre snapshot.
   */
  makeId: number | null;
  make: string;
  modelId: number | null;
  model: string;
  year: string;
  color: string;
  notes: string;
  reason: string;
}

const emptyForm: VehicleForm = {
  customerId: null,
  customerSearch: "",
  plate: "",
  vin: "",
  makeId: null,
  make: "",
  modelId: null,
  model: "",
  year: "",
  color: "",
  notes: "",
  reason: "",
};

/**
 * F-vehicles-edsp — cores FIXAS dos chips/botoes de status (padrao eDSP:
 * Reparado laranja, Faturado vermelho, Devis verde, Cadastro azul; Recebido e
 * bonus do Gestion). Fixas para nao sumirem no tema claro.
 */
const STATUS_ORDER = [
  "em_reparacao",
  "reparado",
  "faturado",
  "recebido_pendente",
  "recebido_pago",
  "devis",
  "cadastro",
] as const;
type VehicleStatus = (typeof STATUS_ORDER)[number];

export default function Vehicles() {
  const { user } = useAuth();
  const canEdit = useCanEdit();
  const companiesQuery = trpc.companies.list.useQuery();
  const accessibleCompanyIds = user?.allowedCompanyIds ?? [];
  const accessibleCompanies = (companiesQuery.data ?? []).filter((c) =>
    accessibleCompanyIds.includes(c.id),
  );

  const [companyId, setCompanyId] = useSelectedCompany(accessibleCompanies);
  const [q, setQ] = useState("");
  const [activeFilter, setActiveFilter] = useState<"active" | "inactive">("active");
  const [statusFilter, setStatusFilter] = useState<VehicleStatus | null>(null);
  const [openMenuId, setOpenMenuId] = useState<number | null>(null);
  // CEO 2026-10-26 — clicar no veículo abre a FICHA dele (todos os dossiers),
  // como no eDSP; antes abria só o último dossier.
  const [fichaVeiculo, setFichaVeiculo] = useState<number | null>(null);
  const [offset, setOffset] = useState(0);

  const filters = useMemo(
    () => ({
      companyId: companyId ?? 0,
      q: q || undefined,
      status: statusFilter ?? undefined,
      active: activeFilter === "active",
      limit: PAGE_SIZE,
      offset,
    }),
    [companyId, q, statusFilter, activeFilter, offset],
  );

  const listQuery = trpc.vehicles.edspList.useQuery(filters, {
    enabled: companyId !== null,
  });
  const utils = trpc.useUtils();

  const [modal, setModal] = useState<
    { mode: "create" } | { mode: "edit"; vehicleId: number } | null
  >(null);
  const t = useT();

  if (!user) return null;
  if (accessibleCompanies.length === 0) {
    return (
      <Shell>
        <p className="text-sm text-neutral-400">{t("vehicles.noCompanyAccess")}</p>
      </Shell>
    );
  }

  const data = listQuery.data;
  const total = data?.total ?? 0;

  return (
    <Shell>
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <FilterField label={t("vehicles.companyLabel")}>
          <select
            value={companyId ?? ""}
            onChange={(e) => {
              setCompanyId(Number(e.target.value));
              setOffset(0);
            }}
            className="rounded-md border border-neutral-800 bg-neutral-900 px-3 py-1.5 text-sm focus:border-lime outline-none w-64"
          >
            {accessibleCompanies.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </FilterField>
        {canEdit && (
          <button
            onClick={() => setModal({ mode: "create" })}
            className="rounded-md bg-lime hover:bg-lime-bright text-on-lime px-4 py-1.5 text-sm font-medium"
          >
            + {t("vehicles.newBtn")}
          </button>
        )}
      </div>

      <FilterPanel testId="vehicles-filter-panel">
        <div className="flex flex-wrap items-end gap-x-4 gap-y-3">
          <FilterField label={t("vehicles.searchLabel")} className="grow max-w-xs">
            <input
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
                setOffset(0);
              }}
              placeholder={t("vehicles.searchPlaceholder")}
              className={FILTER_INPUT + " w-full"}
            />
          </FilterField>
          <FilterField label={t("vehicles.statusLabel")}>
            <select
              value={activeFilter}
              onChange={(e) => {
                setActiveFilter(e.target.value as typeof activeFilter);
                setOffset(0);
              }}
              className={FILTER_INPUT + " w-full sm:w-40"}
            >
              <option value="active">{t("vehicles.filterActive")}</option>
              <option value="inactive">{t("vehicles.filterInactive")}</option>
            </select>
          </FilterField>
        </div>
      </FilterPanel>

      {/* F-vehicles-edsp — contadores clicaveis por status (padrao eDSP). */}
      <div className="flex flex-wrap justify-center gap-2">
        {STATUS_ORDER.map((st) => {
          const n = data?.counters?.[st] ?? 0;
          if (n === 0 && statusFilter !== st) return null;
          const on = statusFilter === st;
          return (
            <button
              key={st}
              onClick={() => {
                setStatusFilter(on ? null : st);
                setOffset(0);
              }}
              style={{
                background: STATUS_STYLE[st]!.bg,
                color: "#fff",
                opacity: statusFilter && !on ? 0.45 : 1,
                outline: on ? "2px solid #fff" : "none",
                outlineOffset: -3,
              }}
              className="rounded-md px-4 py-1.5 text-sm font-bold shadow-sm"
            >
              {t(STATUS_STYLE[st]!.label as Parameters<typeof t>[0])}{" "}
              <span className="ml-1">{n}</span>
            </button>
          );
        })}
      </div>

      {/* CEO 2026-10-13: o `overflow-hidden` cortava o menu ⋮ (só o título
          aparecia). Sem ele o menu abre por cima da lista e da página. */}
      <div className="rounded-lg border border-neutral-800 bg-neutral-900">
        {listQuery.isLoading && (
          <p className="p-4 text-sm text-neutral-500">{t("vehicles.loading")}</p>
        )}
        {listQuery.error && <p className="p-4 text-sm text-red-400">{listQuery.error.message}</p>}
        {data && data.rows.length === 0 && (
          <p className="p-4 text-sm text-neutral-500">{t("vehicles.emptyState")}</p>
        )}
        {data && data.rows.length > 0 && (
          <div className="divide-y divide-neutral-800">
            {data.rows.map((v) => {
              const st = STATUS_STYLE[v.status] ?? STATUS_STYLE.cadastro!;
              const inner = (
                <>
                  <CarLogo make={v.make} size={38} />
                  <span className="min-w-0 grow">
                    <span className="block truncate text-xs text-neutral-400">
                      {[v.make, v.model].filter(Boolean).join(" ") || "—"}
                    </span>
                    <span className="block font-mono text-sm font-bold tracking-wide">
                      {v.plate ?? v.vin ?? "—"}
                    </span>
                    {v.plate && v.vin && (
                      <span className="block truncate font-mono text-[10px] text-neutral-600">
                        {v.vin}
                      </span>
                    )}
                    {v.amount > 0 && (
                      <span className="block text-xs text-neutral-300">
                        {formatMoney(v.amount)}
                      </span>
                    )}
                  </span>
                  {/* CEO 2026-10-26 (modelo eDSP) — um card sólido por dossier
                      do veículo, do mais novo ao mais antigo. */}
                  <span className="flex shrink-0 flex-wrap justify-end gap-1">
                    {/* CEO 2026-10-28 — um card por REPARO (espelhos da BS juntos). */}
                    {(v.repairs.length > 0
                      ? v.repairs
                      : [{ id: 0, status: v.status, partes: 1, faturadas: 0 }]
                    ).map((dz) => {
                      const sd = STATUS_STYLE[dz.status] ?? st;
                      const parcial = dz.partes > 1 && dz.faturadas > 0 && dz.faturadas < dz.partes;
                      return (
                        <span
                          key={dz.id}
                          style={{ background: sd.bg, color: "#fff" }}
                          className="rounded px-2 py-0.5 text-[11px] font-bold"
                        >
                          {t(sd.label as Parameters<typeof t>[0])}
                          {parcial && (
                            <span className="ml-1 font-semibold opacity-90">
                              · {t("dossiers.cardInvoicedPartial")} {dz.faturadas}/{dz.partes}
                            </span>
                          )}
                        </span>
                      );
                    })}
                  </span>
                  <span className="hidden w-64 shrink-0 truncate text-xs uppercase text-neutral-500 sm:block">
                    {v.customerName ?? ""}
                  </span>
                </>
              );
              return (
                <div key={v.id} className="relative flex items-center gap-3 px-3 py-2">
                  {v.latestDossierId ? (
                    <button
                      type="button"
                      onClick={() => setFichaVeiculo(v.id)}
                      className="flex min-w-0 grow items-center gap-3 text-left hover:opacity-90"
                      data-testid={`vehicle-row-${v.id}`}
                    >
                      {inner}
                    </button>
                  ) : (
                    <span className="flex min-w-0 grow items-center gap-3">{inner}</span>
                  )}
                  <button
                    onClick={() => setOpenMenuId(openMenuId === v.id ? null : v.id)}
                    className="shrink-0 rounded-md border border-neutral-800 px-2 py-1 text-sm hover:border-lime"
                    aria-label="menu"
                  >
                    ⋮
                  </button>
                  {openMenuId === v.id && (
                    <>
                      {/* Clique fora fecha o menu. */}
                      <div className="fixed inset-0 z-10" onClick={() => setOpenMenuId(null)} />
                      <div className="absolute right-2 top-10 z-20 w-72 max-w-[calc(100vw-2rem)] rounded-lg border border-neutral-700 bg-neutral-900 p-1.5 shadow-xl">
                        <p className="px-2 py-1 text-[10px] uppercase tracking-wider text-neutral-500">
                          {t("vehicles.menuTitle")}
                        </p>
                        {v.dossiers.length === 0 && (
                          <p className="px-2 py-1 text-xs text-neutral-600">
                            {t("vehicles.noDossiers")}
                          </p>
                        )}
                        {v.dossiers.map((d) => (
                          <Link
                            key={d.id}
                            href={`/dossiers/${d.id}`}
                            className="flex items-center justify-between gap-2 rounded px-2 py-1.5 text-xs hover:bg-neutral-800"
                            onClick={() => setOpenMenuId(null)}
                          >
                            <span className="truncate font-mono">{d.code}</span>
                            <span
                              style={{
                                background: (STATUS_STYLE[d.status] ?? STATUS_STYLE.cadastro!).bg,
                                color: "#fff",
                              }}
                              className="shrink-0 rounded px-1.5 py-0.5 text-[10px] font-bold"
                            >
                              {t(
                                (STATUS_STYLE[d.status] ?? STATUS_STYLE.cadastro!)
                                  .label as Parameters<typeof t>[0],
                              )}
                            </span>
                          </Link>
                        ))}
                        {/* CEO 2026-10-13 — adiantar ao técnico antes da fatura:
                          a proforma não exige fatura nem recebimento do cliente. */}
                        {canEdit && v.latestDossierId && (
                          <Link
                            href={`/proformas/nova?dossierIds=${v.latestDossierId}`}
                            onClick={() => setOpenMenuId(null)}
                            className="mt-1 block w-full rounded border-t border-neutral-800 px-2 py-1.5 text-left text-xs text-lime hover:bg-neutral-800"
                            data-testid="vehicle-request-proforma"
                          >
                            💶 {t("vehicles.requestProforma")}
                          </Link>
                        )}
                        {canEdit && (
                          <button
                            onClick={() => {
                              setOpenMenuId(null);
                              setModal({ mode: "edit", vehicleId: v.id });
                            }}
                            className="mt-1 w-full rounded border-t border-neutral-800 px-2 py-1.5 text-left text-xs text-lime hover:bg-neutral-800"
                          >
                            ✎ {t("vehicles.editLink")}
                          </button>
                        )}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {total > 0 && (
        <Paginacao
          total={total}
          pageSize={PAGE_SIZE}
          offset={offset}
          onOffset={setOffset}
          noun={total === 1 ? t("vehicles.vehicleSingular") : t("vehicles.vehiclePlural")}
          testId="vehicles-pagination"
        />
      )}

      {fichaVeiculo != null && companyId !== null && (
        <VehicleHistoryModal
          companyId={companyId}
          vehicleId={fichaVeiculo}
          onClose={() => setFichaVeiculo(null)}
        />
      )}
      {canEdit && modal && companyId !== null && (
        <VehicleModal
          companyId={companyId}
          mode={modal.mode}
          vehicleId={modal.mode === "edit" ? modal.vehicleId : undefined}
          onClose={() => setModal(null)}
          onSaved={() => {
            setModal(null);
            utils.vehicles.invalidate();
            listQuery.refetch();
          }}
        />
      )}
    </Shell>
  );
}

function VehicleModal({
  companyId,
  mode,
  vehicleId,
  onClose,
  onSaved,
}: {
  companyId: number;
  mode: "create" | "edit";
  vehicleId?: number;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { user } = useAuth();
  const modals = useModals();
  const t = useT();
  const detailQuery = trpc.vehicles.getById.useQuery(
    { companyId, vehicleId: vehicleId ?? 0 },
    {
      enabled: mode === "edit" && !!vehicleId,
      retry: false,
      staleTime: 0,
      refetchOnMount: "always",
    },
  );

  const [form, setForm] = useState<VehicleForm>(emptyForm);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (mode !== "edit") return;
    const d = detailQuery.data;
    if (!d) return;
    setForm({
      customerId: d.customerId,
      customerSearch: "",
      plate: d.plate ?? "",
      vin: d.vin ?? "",
      makeId: d.makeId ?? null,
      make: d.make ?? "",
      modelId: d.modelId ?? null,
      model: d.model ?? "",
      year: d.year ? String(d.year) : "",
      color: d.color ?? "",
      notes: d.notes ?? "",
      reason: "",
    });
  }, [mode, detailQuery.data?.id, detailQuery.dataUpdatedAt]);

  const isAdmin =
    user?.isSuperAdmin ||
    user?.memberships.some((m) => m.companyId === companyId && m.active && m.role === "admin") ||
    false;

  const createMutation = trpc.vehicles.create.useMutation({
    onSuccess: onSaved,
    onError: (err) => setError(err.message),
  });
  const updateMutation = trpc.vehicles.update.useMutation({
    onSuccess: onSaved,
    onError: (err) => setError(err.message),
  });
  const deactivateMutation = trpc.vehicles.deactivate.useMutation({
    onSuccess: onSaved,
    onError: (err) => setError(err.message),
  });
  const reactivateMutation = trpc.vehicles.reactivate.useMutation({
    onSuccess: onSaved,
    onError: (err) => setError(err.message),
  });

  const pending =
    createMutation.isPending ||
    updateMutation.isPending ||
    deactivateMutation.isPending ||
    reactivateMutation.isPending;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    const yearNum = form.year ? Number(form.year) : undefined;
    if (yearNum !== undefined && (Number.isNaN(yearNum) || yearNum < 1900 || yearNum > 2200)) {
      setError(t("vehicles.errInvalidYear"));
      return;
    }
    if (!form.plate.trim() && !form.vin.trim()) {
      setError(t("vehicles.errPlateOrVin"));
      return;
    }
    const payload = {
      companyId,
      customerId: form.customerId ?? undefined,
      plate: form.plate.trim().toUpperCase() || undefined,
      vin: form.vin.trim().toUpperCase() || undefined,
      // Sempre enviamos make/model textual. O backend usa makeId/modelId
      // (quando preenchido) para sobrescrever o snapshot textual via dual-write
      // contra o catálogo. Quando null, fica como texto livre.
      makeId: form.makeId ?? null,
      modelId: form.modelId ?? null,
      make: form.make.trim() || undefined,
      model: form.model.trim() || undefined,
      year: yearNum,
      color: form.color.trim() || undefined,
      notes: form.notes.trim() || undefined,
    };
    if (mode === "create") {
      createMutation.mutate(payload);
    } else if (vehicleId) {
      const { companyId: _drop, ...patch } = payload;
      updateMutation.mutate({
        companyId,
        vehicleId,
        patch,
        reason: form.reason.trim() || undefined,
      });
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50">
      <div className="w-full max-w-2xl bg-neutral-950 border border-neutral-800 rounded-lg shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="px-5 py-4 border-b border-neutral-800 flex items-center justify-between sticky top-0 bg-neutral-950">
          <h2 className="text-lg font-semibold">
            {mode === "create" ? t("vehicles.newBtn") : t("vehicles.editTitle")}
          </h2>
          <button
            onClick={onClose}
            className="text-neutral-500 hover:text-neutral-300 text-lg leading-none"
          >
            ×
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <CustomerPicker
            companyId={companyId}
            selectedId={form.customerId}
            onSelect={(id) => setForm({ ...form, customerId: id, customerSearch: "" })}
          />

          <div className="grid grid-cols-2 gap-3">
            <Field label={t("vehicles.colPlate")}>
              <input
                value={form.plate}
                onChange={(e) => setForm({ ...form, plate: e.target.value.toUpperCase() })}
                maxLength={20}
                disabled={pending}
                className={inputClass}
              />
            </Field>
            <Field label={t("vehicles.vinLabel")}>
              <input
                value={form.vin}
                onChange={(e) => setForm({ ...form, vin: e.target.value.toUpperCase() })}
                maxLength={17}
                disabled={pending}
                className={inputClass}
              />
            </Field>
            <Field label={t("vehicles.colColor")}>
              <input
                value={form.color}
                onChange={(e) => setForm({ ...form, color: e.target.value })}
                maxLength={50}
                disabled={pending}
                className={inputClass}
              />
            </Field>
            <div /> {/* alinhamento da grid 2-col */}
          </div>

          <MakeModelYearPicker form={form} setForm={setForm} disabled={pending} />

          <Field label={t("vehicles.notesLabel")}>
            <textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              maxLength={2000}
              rows={2}
              disabled={pending}
              className={`${inputClass} resize-y`}
            />
          </Field>

          {error && (
            <div className="text-red-400 text-sm bg-red-500/10 border border-red-500/30 rounded-md px-3 py-2">
              {error}
            </div>
          )}

          <div className="flex items-center justify-between pt-2 border-t border-neutral-800">
            <div className="flex gap-2">
              {mode === "edit" && vehicleId && isAdmin && detailQuery.data && (
                <>
                  {detailQuery.data.active ? (
                    <button
                      type="button"
                      onClick={async () => {
                        const reason = await modals.promptReason({
                          title: t("vehicles.deactivateTitle"),
                          message: t("vehicles.deactivateMessage"),
                          placeholder: t("vehicles.deactivatePlaceholder"),
                          confirmLabel: t("vehicles.deactivate"),
                          tone: "danger",
                          required: true,
                        });
                        if (!reason) return;
                        deactivateMutation.mutate({ companyId, vehicleId, reason });
                      }}
                      disabled={pending}
                      className="rounded-md border border-red-500/40 hover:border-red-500 text-red-400 px-3 py-1.5 text-xs"
                    >
                      {t("vehicles.deactivate")}
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => reactivateMutation.mutate({ companyId, vehicleId })}
                      disabled={pending}
                      className="rounded-md border border-lime/40 hover:border-lime text-lime px-3 py-1.5 text-xs"
                    >
                      {t("vehicles.reactivate")}
                    </button>
                  )}
                </>
              )}
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={onClose}
                disabled={pending}
                className="rounded-md border border-neutral-800 hover:border-neutral-700 px-4 py-1.5 text-sm"
              >
                {t("vehicles.cancel")}
              </button>
              <button
                type="submit"
                disabled={pending}
                className="rounded-md bg-lime hover:bg-lime-bright text-on-lime disabled:opacity-50 px-4 py-1.5 text-sm font-medium"
              >
                {pending
                  ? t("vehicles.saving")
                  : mode === "create"
                    ? t("vehicles.create")
                    : t("vehicles.save")}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

/** Autocomplete simples de clientes — usa customers.search. */
function CustomerPicker({
  companyId,
  selectedId,
  onSelect,
}: {
  companyId: number;
  selectedId: number | null;
  onSelect: (id: number | null) => void;
}) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const t = useT();

  const selectedQuery = trpc.customers.getById.useQuery(
    { companyId, customerId: selectedId ?? 0 },
    { enabled: !!selectedId, retry: false },
  );
  const searchQuery = trpc.customers.search.useQuery(
    { companyId, q, limit: 10 },
    { enabled: q.trim().length >= 2 },
  );

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  return (
    <div ref={ref} className="relative">
      <label className="block text-xs text-neutral-400 mb-1">
        {t("vehicles.customerOptionalLabel")}
      </label>
      {selectedId && selectedQuery.data ? (
        <div className="flex items-center gap-2 rounded-md border border-neutral-800 bg-neutral-900 px-3 py-1.5 text-sm">
          <span>{selectedQuery.data.displayName}</span>
          {selectedQuery.data.legalName && (
            <span className="text-xs text-neutral-500">({selectedQuery.data.legalName})</span>
          )}
          <button
            type="button"
            onClick={() => onSelect(null)}
            className="ml-auto text-xs text-red-400 hover:underline"
          >
            {t("vehicles.remove")}
          </button>
        </div>
      ) : (
        <input
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          placeholder={t("vehicles.customerSearchPlaceholder")}
          className={inputClass}
        />
      )}
      {open && q.trim().length >= 2 && !selectedId && (
        <div className="absolute z-10 mt-1 w-full max-h-64 overflow-y-auto rounded-md border border-neutral-800 bg-neutral-950 shadow-lg">
          {searchQuery.isLoading && (
            <p className="p-2 text-xs text-neutral-500">{t("vehicles.searching")}</p>
          )}
          {searchQuery.data && searchQuery.data.length === 0 && (
            <p className="p-2 text-xs text-neutral-500">{t("vehicles.noCustomerFound")}</p>
          )}
          {searchQuery.data?.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => {
                onSelect(c.id);
                setQ("");
                setOpen(false);
              }}
              className="w-full text-left px-3 py-2 hover:bg-neutral-900 text-sm border-b border-neutral-800 last:border-b-0"
            >
              <div>{c.displayName}</div>
              {c.legalName && <div className="text-xs text-neutral-500">{c.legalName}</div>}
              {c.fiscalId && <div className="text-xs text-neutral-600">{c.fiscalId}</div>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const inputClass =
  "w-full rounded-md border border-neutral-800 bg-neutral-900 px-3 py-1.5 text-sm focus:border-lime outline-none disabled:opacity-50";

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="block text-xs text-neutral-400 mb-1">
        {label} {required && <span className="text-red-400">*</span>}
      </label>
      {children}
    </div>
  );
}

/**
 * Marca / Modelo / Ano. Autocomplete livre-texto:
 *  - Usuário digita → dropdown sugere matches do catálogo.
 *  - Clica numa sugestão → vincula ao catálogo (makeId/modelId preenchidos).
 *  - Digita e sai sem clicar → texto livre (FK null, valor textual preservado).
 * Pattern espelha CustomerPicker. Trocar a marca limpa o modelo.
 * Ano não-obrigatório; warning amarelo (não-bloqueante) se houver modelId e
 * o ano cair fora dos ranges conhecidos.
 */
function MakeModelYearPicker({
  form,
  setForm,
  disabled,
}: {
  form: VehicleForm;
  setForm: (f: VehicleForm) => void;
  disabled: boolean;
}) {
  const t = useT();
  const yearNum = form.year ? Number(form.year) : NaN;
  const yearCheckQuery = trpc.vehicleCatalog.checkYearRange.useQuery(
    { modelId: form.modelId ?? 0, year: Number.isFinite(yearNum) ? yearNum : 0 },
    {
      enabled:
        form.modelId !== null && Number.isFinite(yearNum) && yearNum >= 1900 && yearNum <= 2200,
      staleTime: 60_000,
    },
  );

  return (
    <div className="grid grid-cols-2 gap-3">
      <MakePicker
        makeId={form.makeId}
        makeText={form.make}
        disabled={disabled}
        onPick={(picked) =>
          setForm({
            ...form,
            makeId: picked.makeId,
            make: picked.text,
            // Trocou marca → limpa modelo (mesmo se foi só edição de texto).
            modelId: null,
            model: "",
          })
        }
      />

      <ModelPicker
        makeId={form.makeId}
        makeText={form.make}
        modelId={form.modelId}
        modelText={form.model}
        disabled={disabled}
        onPick={(picked) => setForm({ ...form, modelId: picked.modelId, model: picked.text })}
      />

      <Field label={t("vehicles.colYear")}>
        <input
          type="number"
          value={form.year}
          onChange={(e) => setForm({ ...form, year: e.target.value })}
          min={1900}
          max={2200}
          disabled={disabled}
          className={inputClass}
        />
        {yearCheckQuery.data && yearCheckQuery.data.ok === false && (
          <p className="text-xs text-amber-400 mt-1">⚠ {yearCheckQuery.data.message}</p>
        )}
        {yearCheckQuery.data?.ok === true &&
          "generationLabel" in yearCheckQuery.data &&
          yearCheckQuery.data.generationLabel && (
            <p className="text-xs text-neutral-500 mt-1">
              {t("vehicles.generationLabel")}: {yearCheckQuery.data.generationLabel}
            </p>
          )}
      </Field>
      <div />
    </div>
  );
}

/** Autocomplete de marca — pattern CustomerPicker, com fallback texto livre. */
function MakePicker({
  makeId,
  makeText,
  disabled,
  onPick,
}: {
  makeId: number | null;
  makeText: string;
  disabled: boolean;
  onPick: (picked: { makeId: number | null; text: string }) => void;
}) {
  const [q, setQ] = useState(makeText);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const t = useT();

  // Sincroniza input com `makeText` externo (ex: detail carrega no edit mode).
  useEffect(() => {
    setQ(makeText);
  }, [makeText]);

  const searchQuery = trpc.vehicleCatalog.searchMakes.useQuery(
    { q: q.trim(), limit: 15 },
    { enabled: open, staleTime: 60_000 * 10 },
  );

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  return (
    <div ref={ref} className="relative">
      <label className="block text-xs text-neutral-400 mb-1">{t("vehicles.makeLabel")}</label>
      <div className="relative">
        <input
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            // Mudou o texto → desfaz vínculo com catálogo (vira texto livre até clicar numa sugestão).
            if (makeId !== null) onPick({ makeId: null, text: e.target.value });
            else onPick({ makeId: null, text: e.target.value });
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          placeholder={t("vehicles.makePlaceholder")}
          disabled={disabled}
          maxLength={100}
          className={inputClass}
        />
        {makeId !== null && (
          <span
            className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] uppercase tracking-wider text-lime/80 bg-lime/10 px-1.5 py-0.5 rounded"
            title={t("vehicles.catalogLinkedTitle")}
          >
            {t("vehicles.catalogBadge")}
          </span>
        )}
      </div>
      {open && (
        <div className="absolute z-10 mt-1 w-full max-h-64 overflow-y-auto rounded-md border border-neutral-800 bg-neutral-950 shadow-lg">
          {searchQuery.isLoading && (
            <p className="p-2 text-xs text-neutral-500">{t("vehicles.searching")}</p>
          )}
          {searchQuery.data && searchQuery.data.length === 0 && (
            <p className="p-2 text-xs text-neutral-500">{t("vehicles.noMakeInCatalog")}</p>
          )}
          {searchQuery.data?.map((m) => (
            <button
              key={m.id}
              type="button"
              onClick={() => {
                onPick({ makeId: m.id, text: m.displayName });
                setQ(m.displayName);
                setOpen(false);
              }}
              className="w-full text-left px-3 py-2 hover:bg-neutral-900 text-sm border-b border-neutral-800 last:border-b-0 flex items-center gap-2"
            >
              <span className="flex-1">{m.displayName}</span>
              {m.countryOrigin && (
                <span className="text-xs text-neutral-600">{m.countryOrigin}</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** Autocomplete de modelo dependente da marca selecionada (ou texto livre). */
function ModelPicker({
  makeId,
  makeText,
  modelId,
  modelText,
  disabled,
  onPick,
}: {
  makeId: number | null;
  makeText: string;
  modelId: number | null;
  modelText: string;
  disabled: boolean;
  onPick: (picked: { modelId: number | null; text: string }) => void;
}) {
  const [q, setQ] = useState(modelText);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const t = useT();

  useEffect(() => {
    setQ(modelText);
  }, [modelText]);

  // Só busca no catálogo se há makeId vinculado. Para marcas em texto livre,
  // o modelo também só pode ser livre — o dropdown não dispara.
  const searchQuery = trpc.vehicleCatalog.searchModels.useQuery(
    { makeId: makeId ?? 0, q: q.trim(), limit: 15 },
    { enabled: open && makeId !== null, staleTime: 60_000 * 10 },
  );

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const hint =
    makeId === null
      ? makeText
        ? t("vehicles.hintMakeFreeText")
        : t("vehicles.hintChooseMakeFirst")
      : null;

  return (
    <div ref={ref} className="relative">
      <label className="block text-xs text-neutral-400 mb-1">{t("vehicles.modelLabel")}</label>
      <div className="relative">
        <input
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            onPick({ modelId: null, text: e.target.value });
            if (makeId !== null) setOpen(true);
          }}
          onFocus={() => makeId !== null && setOpen(true)}
          placeholder={
            makeId !== null ? t("vehicles.modelPlaceholder") : t("vehicles.modelFreePlaceholder")
          }
          disabled={disabled}
          maxLength={100}
          className={inputClass}
        />
        {modelId !== null && (
          <span
            className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] uppercase tracking-wider text-lime/80 bg-lime/10 px-1.5 py-0.5 rounded"
            title={t("vehicles.catalogLinkedTitle")}
          >
            {t("vehicles.catalogBadge")}
          </span>
        )}
      </div>
      {hint && <p className="text-xs text-neutral-600 mt-1">{hint}</p>}
      {open && makeId !== null && (
        <div className="absolute z-10 mt-1 w-full max-h-64 overflow-y-auto rounded-md border border-neutral-800 bg-neutral-950 shadow-lg">
          {searchQuery.isLoading && (
            <p className="p-2 text-xs text-neutral-500">{t("vehicles.searching")}</p>
          )}
          {searchQuery.data && searchQuery.data.length === 0 && (
            <p className="p-2 text-xs text-neutral-500">{t("vehicles.noModelInCatalog")}</p>
          )}
          {searchQuery.data?.map((m) => (
            <button
              key={m.id}
              type="button"
              onClick={() => {
                onPick({ modelId: m.id, text: m.displayName });
                setQ(m.displayName);
                setOpen(false);
              }}
              className="w-full text-left px-3 py-2 hover:bg-neutral-900 text-sm border-b border-neutral-800 last:border-b-0 flex items-center gap-2"
            >
              <span className="flex-1">{m.displayName}</span>
              {m.bodyType && <span className="text-xs text-neutral-600">{m.bodyType}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <ErpShell>
      <div className="space-y-4">{children}</div>
    </ErpShell>
  );
}

function ActiveBadge({ active }: { active: boolean }) {
  const t = useT();
  return active ? (
    <span className="inline-block rounded bg-lime/20 text-lime px-2 py-0.5 text-xs">
      {t("vehicles.badgeActive")}
    </span>
  ) : (
    <span className="inline-block rounded bg-neutral-700/40 text-neutral-400 px-2 py-0.5 text-xs">
      {t("vehicles.badgeInactive")}
    </span>
  );
}
